import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Search, Mail, Phone, Building2, Calendar, MessageSquare, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fg_admin_token";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMe(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); })
      .finally(() => setChecking(false));
  }, [token]);

  const onLogin = (t, email) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setMe({ email, role: "admin" });
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMe(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-navy text-[hsl(var(--background))]">
        <span className="font-serif text-2xl italic">Verifying session…</span>
      </div>
    );
  }
  if (!token || !me) return <LoginView onLogin={onLogin} />;
  return <Dashboard token={token} me={me} onLogout={onLogout} />;
}

// ---------------- Login ----------------
function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("Enter email and password."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.token, data.email);
      toast.success("Signed in.");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy text-[hsl(var(--background))]">
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative overflow-hidden p-14 flex-col justify-between">
        <div className="flex items-center gap-3">
          <span className="bg-white rounded-md p-1.5 inline-flex">
            <img src="/assets/fg-logo.png" alt="Faith & Grace" className="h-10 w-auto" />
          </span>
          <span className="font-serif text-lg">Faith <span className="text-gold">&amp;</span> Grace Advisory Services</span>
        </div>
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-6">Admin Console</p>
          <h1 className="font-serif font-light text-5xl leading-tight mb-6">
            Every inquiry, <span className="italic text-gold">safely captured.</span>
          </h1>
          <p className="text-[hsl(var(--background)/0.65)] max-w-md">
            Sign in to review consultation requests submitted through faithandgraceadvisoryservices.com.
          </p>
        </div>
        <p className="text-xs text-[hsl(var(--background)/0.4)]">© {new Date().getFullYear()} Faith &amp; Grace Advisory Services, LLC</p>
      </div>

      {/* Right: form */}
      <div className="grid place-items-center p-8 lg:p-14 bg-[hsl(var(--background))] text-primary">
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <img src="/assets/fg-logo.png" alt="" className="h-10 w-auto" />
            <span className="font-serif text-lg">Faith &amp; Grace</span>
          </div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-4 flex items-center gap-2">
            <Shield size={14} /> Admin Sign-in
          </p>
          <h2 className="font-serif font-light text-4xl leading-tight mb-10">Welcome back.</h2>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs tracking-wide text-muted-foreground">Email</Label>
              <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@faithandgraceadvisoryservices.com" className="rounded-none" autoComplete="email" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wide text-muted-foreground">Password</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="rounded-none" autoComplete="current-password" />
            </div>
            {err && <p data-testid="admin-login-error" className="text-sm text-destructive">{err}</p>}
            <button type="submit" data-testid="admin-login-btn" disabled={loading}
              className="w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-medium hover:bg-[hsl(var(--gold))] hover:text-primary transition-colors duration-300 disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </div>

          <a href="/" className="mt-8 inline-block text-sm text-muted-foreground link-underline">← Back to site</a>
        </form>
      </div>
    </div>
  );
}

// ---------------- Dashboard ----------------
function Dashboard({ token, me, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(data);
      if (data.length && !active) setActive(data[0]);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      toast.error("Could not load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.organization, r.services, r.message].some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [rows, q]);

  const fmt = (v) => {
    try {
      return new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return v; }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--muted)/0.4)] text-primary">
      {/* Header */}
      <header className="bg-navy text-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md p-1.5 inline-flex">
              <img src="/assets/fg-logo.png" alt="" className="h-9 w-auto" />
            </span>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-gold">Admin Console</p>
              <p className="font-serif text-lg leading-tight">Faith &amp; Grace Advisory</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden md:inline text-sm text-[hsl(var(--background)/0.7)]" data-testid="admin-me-email">{me.email}</span>
            <button data-testid="admin-logout-btn" onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--background)/0.25)] px-4 py-2 text-sm hover:border-gold hover:text-gold transition-colors">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-2">Consultation Requests</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl leading-tight">Inbox</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground" data-testid="admin-count">{filtered.length} of {rows.length}</span>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, service…" className="rounded-none pl-9 w-64" />
            </div>
          </div>
        </div>

        {/* Layout: list + detail */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 bg-background border border-border">
            {loading ? (
              <p className="p-8 text-muted-foreground" data-testid="admin-loading">Loading inquiries…</p>
            ) : filtered.length === 0 ? (
              <p className="p-8 text-muted-foreground" data-testid="admin-empty">No inquiries yet.</p>
            ) : (
              <ul>
                {filtered.map((r) => (
                  <li key={r.id}>
                    <button data-testid={`admin-row-${r.id}`} onClick={() => setActive(r)}
                      className={`w-full text-left px-6 py-5 border-b border-border hover:bg-[hsl(var(--muted)/0.5)] transition-colors ${
                        active?.id === r.id ? "bg-[hsl(var(--muted)/0.6)] border-l-2 border-l-[hsl(var(--gold))]" : ""
                      }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-serif text-lg leading-tight truncate">{r.name}</p>
                          <p className="text-xs text-muted-foreground mt-1 truncate">{r.services}</p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {active ? (
              <div className="bg-background border border-border p-8" data-testid="admin-detail">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-2">{active.services}</p>
                    <h2 className="font-serif text-3xl leading-tight">{active.name}</h2>
                  </div>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Calendar size={12} />{fmt(active.created_at)}</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-8">
                  <Field icon={<Mail size={14} />} label="Email">
                    <a href={`mailto:${active.email}`} className="link-underline">{active.email}</a>
                  </Field>
                  <Field icon={<Phone size={14} />} label="Phone">
                    <a href={`tel:${active.phone}`} className="link-underline">{active.phone}</a>
                  </Field>
                  <Field icon={<Building2 size={14} />} label="Organization">
                    <span>{active.organization || "—"}</span>
                  </Field>
                </div>

                <div>
                  <p className="text-xs tracking-wide uppercase text-muted-foreground mb-3 inline-flex items-center gap-2"><MessageSquare size={13} /> Message</p>
                  <div className="border-l-2 border-[hsl(var(--gold))] pl-5 py-1 font-light leading-relaxed text-primary whitespace-pre-wrap">
                    {active.message}
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a href={`mailto:${active.email}?subject=Re: Your consultation request`}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(var(--gold))] hover:text-primary transition-colors">
                    <Mail size={14} /> Reply by Email
                  </a>
                  <a href={`tel:${active.phone}`}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/25 px-5 py-2.5 text-sm hover:border-[hsl(var(--gold))] transition-colors">
                    <Phone size={14} /> Call
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-background border border-border p-12 text-center text-muted-foreground">
                Select an inquiry to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon, label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1.5 mb-1.5">{icon}{label}</p>
    <div className="text-primary">{children}</div>
  </div>
);
